/* @flow */
import React, { useMemo } from "react";
import idx from "idx";
import styled from "styled-components";
import querystring from "querystring";
import { Redirect } from "react-router-dom";
import { connect } from "react-redux";
import withData from "../components/withData";
import { Server, get } from "../components/common/fetch";
import Gettext from "../components/common/Gettext";
import Loadable from "../components/Loadable";
import Header from "../components/UnitedHeader";
import ErrorPopup from "../components/ErrorPopup/Error";
import {
  FillupConfiguration,
  getPaymentBonus,
  getBillingBonus
} from "../components/FillupConfiguration";
import { RawAuth } from "../components/Auth/Auth";
import Private from "../components/Private";
import OfflinePopup from "../components/OfflinePopup/OfflinePopup";

import type { UnrolledConfig, Promo } from "../components/FillupConfiguration";
import { gaReducer } from "../components/GA";
import { animateChatButton } from "../components/Chat";
import WarningIcon from "../components/Icons/Warning";

import Spinner from "../components/Spinners/BrandSpinner";
import TournamentApi from "../components/Tournaments/Api";
import { isWebApp } from "../components/common/Link";
import { isIronTerminal } from "../components/utils";

function writePromoCode(code) {
  try {
    localStorage.setItem("promocode", code.toUpperCase());
  } catch (error) {}
}

function readPromoCode() {
  let query = querystring.parse(window.location.search.slice(1));

  if (query.promocode) {
    return query.promocode;
  } else {
    let code = null;
    try {
      code = localStorage.getItem("promocode");
    } catch (error) {}
    return code || "";
  }
}

export const PromoContext = React.createContext({
  onValidatePromo: () => {},
  isPromoApplied: false,
  isFetchPromoInProgress: false,
  promoCode: "",
  onInputPromo: () => {},
  promo_error: false,
  promo: {}
});

const Center = styled.h1`
  margin: 40px;
  text-align: center;
`;

const ErrorWrapper = styled.h1`
  border: 2px solid #e74c3c;
  margin: 30px auto 30px;
  padding: 30px;
  max-width: 800px;
  box-sizing: border-box;

  @media (max-width: 425px) {
    border: none;
    margin: 30px 0;
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 30px;
  color: #e74c3c;
`;

const ErrorMess = styled.div`
  font-size: 25px;
  color: #e74c3c;
`;

// Делаем лэйауты асинхронными

const defaultDesktopLayout = Loadable(
  () =>
    import(/* webpackChunkName: "fillup-5" */ "../components/Fillup/Desktop365Variant1/Layout"),
  "components/Fillup/Desktop365Variant1/Layout"
);

const defaultMobileLayout = Loadable(
  () =>
    import(/* webpackChunkName: "fillup-11" */ "../components/Fillup/MobileWalkthrough11/Layout"),
  "components/Fillup/MobileWalkthrough11/Layout"
);

const alwaysPortraitMobileLayout = Loadable(
  () =>
    import(/* webpackChunkName: "fillup-20" */ "../components/Fillup/MobileWalkthrough11/PortraitLayout"),
  "components/Fillup/MobileWalkthrough11/PortraitLayout"
);

const alwaysLandscapeMobileLayout = Loadable(
  () =>
    import(/* webpackChunkName: "fillup-21" */ "../components/Fillup/MobileWalkthrough11/LandscapeLayout"),
  "components/Fillup/MobileWalkthrough11/LandscapeLayout"
);

const oldDesktopLayout = Loadable(
  () =>
    import(/* webpackChunkName: "fillup-4" */ "../components/Fillup/Desktop/Desktop"),
  "components/Fillup/Desktop/Desktop"
);

const newDesktopLayout2 = Loadable(
  () =>
    import(/* webpackChunkName: "fillup-6" */ "../components/Fillup/Desktop365Variant2/Layout"),
  "components/Fillup/Desktop365Variant2/Layout"
);

const newMobileLayout = Loadable(
  () =>
    import(/* webpackChunkName: "fillup-13" */ "../components/Fillup/Mobile365/Layout"),
  "components/Fillup/Mobile365/Layout"
);

const desktopStepsLayout = Loadable(() =>
  import(/* webpackChunkName: "fillup-7" */ "../components/Fillup/Desktop365Variant1/LayoutSteps")
);

const mobileAmountInputLayout = Loadable(() =>
  import(/* webpackChunkName: fillup-14 */ "../components/Fillup/MobileAmountInput/Layout")
);

const mobileWithTipsLayout = Loadable(() =>
  import(/* webpackChunkName: fillup-15 */ "../components/Fillup/MobileWithTips/Layout")
);

const payTotalLayout = Loadable(() =>
  import(/* webpackChunkName: fillup-15 */ "../components/Fillup/PayTotal")
);

const layouts = {
  "4": oldDesktopLayout,
  "5": defaultDesktopLayout,
  "6": newDesktopLayout2,
  "7": desktopStepsLayout,
  "8": newDesktopLayout2,
  "9": defaultMobileLayout,
  "10": defaultMobileLayout,
  "11": defaultMobileLayout,
  "12": defaultMobileLayout,
  "13": newMobileLayout,
  "14": mobileAmountInputLayout,
  "15": mobileWithTipsLayout,
  "16": newMobileLayout,
  "17": mobileWithTipsLayout,
  "20": alwaysPortraitMobileLayout,
  "21": alwaysLandscapeMobileLayout,
  "22": payTotalLayout,
  "23": newDesktopLayout2,
  "24": newMobileLayout,
  "25": oldDesktopLayout,
  "26": defaultMobileLayout,
  "27": newDesktopLayout2,
  "28": mobileWithTipsLayout,
  "29": newDesktopLayout2,
  "30": mobileWithTipsLayout
};

function toDefault<T: number | string>(value: T | null, defaultValue: T): T {
  if (value != null) {
    return value;
  }
  return defaultValue;
}

function animatedScrollTo(destination) {
  const startTime = Date.now();
  const startY = window.scrollY;

  const acceleration = startY / (destination * destination);
  const startSpeed = -startY / destination - acceleration * destination;

  requestAnimationFrame(function scroll() {
    const now = Date.now();
    const elapsedTime = now - startTime;

    const y =
      startY +
      startSpeed * elapsedTime +
      acceleration * elapsedTime * elapsedTime;

    window.scrollTo(0, y);

    if (elapsedTime < 500) {
      requestAnimationFrame(scroll);
    }
  });
}

function isValidTextValue(value: ?string): %checks {
  return typeof value === "string" && value.trim() !== "";
}

function submitPost(url) {
  let [action, query] = url.split("?");
  let params = querystring.parse(query);
  let form = document.createElement("form");
  form.action = action;
  form.method = "post";
  let keys = Object.keys(params);
  for (let key of keys) {
    let input = document.createElement("input");
    input.type = "text";
    input.name = key;
    input.value = params[key];
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

type Props = {
  config: UnrolledConfig,
  fetch: (method: string, options?: Object) => Promise<Object>
};

type ErrorMessage = string | React.Node;

type State = {
  deposit: number | null,
  billingType: string | null,
  bank: ?string,
  requestPhone: boolean,
  vodafoneCode: ?string,
  fullName: ?string,
  phoneNumber: ?string,
  isBonusApplied: boolean,
  general_error: ?ErrorMessage,
  fullName_error: ?ErrorMessage,
  pan_error: ?ErrorMessage,
  phoneNumber_error: ?ErrorMessage,
  server_error: ?ErrorMessage,
  redirectTo: string | null,
  isFetchInProgress: boolean,
  isPromoModalActive: boolean,
  isFetchPromoInProgress: boolean,
  isInitialFetchPromoInProgress: boolean,
  promo: ?Promo
};

// Лэйауты наследуют большинство стейта, но, в отличие от стейта
export type FillupObject = {|
  config: UnrolledConfig,
  deposit: number, // депозит и способ оплаты должны
  billingType: string, // быть определены однозначно
  bank: ?string,
  fullName: ?string,
  phoneNumber: ?string,
  isBonusApplied: boolean,
  general_error: ?ErrorMessage,
  fullName_error: ?ErrorMessage,
  pan_error: ?ErrorMessage,
  phoneNumber_error: ?ErrorMessage,
  onNextPaymentValue: Function,
  onNextBillingType: Function,
  onNextBank: Function,
  onNextPaymentPoint: Function,
  onFullNameChange: Function,
  onPhoneNumberChange: Function,
  onBonusSwitch: Function,
  onSubmit: Function,
  promo?: Object
|};

export type LayoutProps = {
  ...FillupObject,
  fillup: FillupObject
};

class Fillup extends React.Component<Props, State> {
  shouldAnimateChatButton = true;
  promoErrorCount = 0;

  constructor(props) {
    super(props);

    const { first_name, phone, phone_confirmed } =
      props.profile != null
        ? props.profile
        : { first_name: null, phone: null, phone_confirmed: 0 };
    //Если поля существуют и не пустые строки то в стейте ставим
    //их в null(!) для дальнейшего перфоманса с проверками

    this.state = {
      showOfflinePopup: false,
      offlineBanks: null,
      deposit: null,
      billingType: null,
      bank: props.config.defaultBank,
      requestPhone: false,
      vodafoneCode: null,
      fullName: isValidTextValue(first_name) ? null : "",
      phoneNumber: isValidTextValue(phone) ? null : "",
      phoneVerified: phone_confirmed === 1,
      scratchCardProvider: null,
      scratchCardNumber: "",
      scratchCardPIN: "",
      isBonusApplied: true,
      server_error: null,
      general_error: null,
      fullName_error: null,
      pan_error: null,
      phoneNumber_error: null,
      redirectTo: null,
      isFetchInProgress: false,
      isPromoModalActive: false,
      isFetchPromoInProgress: false,
      isInitialFetchPromoInProgress: false,
      promoCode: readPromoCode(),
      promo_error: null,
      promo: null,
      isInfoModalShown: false,
      displayCreditCardForm: false,
      creditCardData: null,
      showInrInfo: true,
      customer_pan_number:null,
    };
  }

  // componentWillReceiveProps(nextProps) {
  //   if (nextProps.profile !== this.props.profile && nextProps.profile != null) {
  //     const { first_name, phone } = nextProps.profile;
  //     //Если поля существуют и не пустые строки то в стейте ставим
  //     //их в null(!) для дальнейшего перфоманса с проверками
  //
  //     this.setState({
  //       fullName: isValidTextValue(first_name) ? null : "",
  //       phoneNumber: isValidTextValue(phone) ? null : ""
  //     });
  //   }
  // }

  userPlatform = () => {
    const ua = window.navigator.userAgent.toLowerCase();
    // iOS Terminal
    const isIosTerminal = /com.ironterminal.ios/g.test(ua);
    // Android Webview full app
    const isWebView = /wvbo_full/g.test(ua);
    // Android Webview analytics
    const isAnalyticsWebView =
      process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
      /androidweb.*com\.irontrade\.analytics.*wvbo/g.test(ua) &&
      !isWebView;
    // iOS Webview Analytics
    const isIosAnalyticsWebView = /iosweb.*com\.full\.iron.*wvbo_full/.test(ua);

    switch (true) {
      case this.props.serverData.isIOS && this.props.serverData.app_version > 0:
        return "webview-ios";
      case isIosTerminal:
        return "webview-ios-terminal";
      case isIosAnalyticsWebView:
        return "webview-ios-analytics";
      case isWebView:
      case this.props.serverData.isAndroidApp:
        return "webview-android";
      case isAnalyticsWebView:
        return "webview-android-analytics";
      case isWebApp():
        return "webapp";
      case this.props.serverData.isMobile:
        return "web-mobile";
      default:
        return "web-desktop";
    }
  };

  setShowOfflinePopup = show => {
    this.setState({ showOfflinePopup: show });
  };

  handleNextPaymentValue = (deposit: number) => {
    this.setState({ deposit });
  };

  handleNextPanValue = (customer_pan_number: string) => {
    this.setState({ customer_pan_number });
  };

  handleNextBillingType = (billingType: string) => {
    this.setState({
      billingType,
      scratchCardProvider: null,
      bank: null
    });
  };

  handleNextBank = (bank: string) => {
    this.setState({
      bank,
      general_error: null
    });
  };

  handleRequestPhone = (val: string) => {
    this.setState({
      requestPhone: val
    });
  };

  handleVodafoneCode = (code: string) => {
    this.setState({
      vodafoneCode: code
    });
  };

  handleFullNameChange = (fullName: string) => {
    this.setState({
      fullName,
      fullName_error: null
    });
  };

  handleErrorPopupDismiss = () => {
    this.setState({
      server_error: null
    });
  };

  handlePhoneNumberChange = (phoneNumber: string) => {
    this.setState({
      phoneNumber,
      phoneNumber_error: null
    });
  };

  validatePhoneNumber = (phoneVerified: boolean) => {
    this.setState({ phoneVerified });
    this.props.dispatch({ type: "REQUEST_PROFILE" });
  };

  handleScratchCardProviderChange = (scratchCardProvider: string) => {
    this.setState({ scratchCardProvider });
  };

  handleScratchCardNumberChange = (scratchCardNumber: number) => {
    this.setState({
      scratchCardNumber: scratchCardNumber.replace(/[^0-9a-zA-Z]/g, ""),
      scratchCard_error: false
    });
  };

  handleScratchCardPINChange = (scratchCardPIN: string) => {
    this.setState({
      scratchCardPIN: scratchCardPIN.replace(/[^0-9a-zA-Z]/g, ""),
      scratchCard_error: false
    });
  };

  handleBonusSwitch = (isBonusApplied: boolean) => {
    this.setState({ isBonusApplied });
  };

  handleCreditCard = creditCardData => {
    this.setState(
      {
        creditCardData
      },
      () => {
        this.hanldleFormSubmission();
      }
    );
  };

  hanldleFormSubmission = () => {
    if (this.state.isFetchInProgress) {
      return false;
    }

    const currentBillingType = toDefault(
      this.state.billingType,
      this.props.config.defaultBilling
    );
    const currentBilling = this.props.config.billings[currentBillingType];
    if (!currentBilling) {
      return;
    }

    const profileFieldsContainer = document.getElementById("field_container");

    if (
      currentBillingType === "ipaytotal" &&
      !this.state.displayCreditCardForm
    ) {
      this.setState({
        displayCreditCardForm: true
      });

      return false;
    }

    this.setState(
      {
        isFetchInProgress: true
      },
      () => {
        // Валидации

        if (currentBilling.needsPanNumber) {
          if (!this.state.customer_pan_number){
            this.setState({
              isFetchInProgress: false,
              pan_error: <Gettext>Fill in PAN</Gettext>
            });

            animatedScrollTo(800);
            return;
          } else {
            let rExp = /^([a-zA-Z]{5})[0-9]{4}[a-zA-Z]{1}$/;
            let test = rExp.test(this.state.customer_pan_number);
            if (!test) {
              this.setState({
                isFetchInProgress: false,
                pan_error: <Gettext>Fill in correct PAN</Gettext>
              });

              animatedScrollTo(800);
              return;
            }
          }
        }

        // Если надо было выбрать банк,
        // а ты ничего даже и не выбрал
        if (
          this.props.config.banksSettings &&
          this.props.config.banksSettings[currentBillingType] != null &&
          this.state.bank === null
        ) {
          let settings = this.props.config.banksSettings[currentBillingType];

          if (settings.banks != null && this.state.bank == null) {
            this.setState({
              isFetchInProgress: false,
              general_error: <Gettext>Choose bank</Gettext>
            });

            animatedScrollTo(800);

            return;
          } else if (
            settings.payment_points != null &&
            this.state.bank == null
          ) {
            this.setState({
              isFetchInProgress: false,
              general_error: <Gettext>Choose payment point</Gettext>
            });

            animatedScrollTo(800);

            return;
          } else if (settings.provider != null && this.state.bank == null) {
            this.setState({
              isFetchInProgress: false,
              general_error: <Gettext>Choose phone provider</Gettext>
            });

            animatedScrollTo(800);

            return;
          }
        }

        if (currentBilling.is_scratch === true) {
          if (
            !this.state.scratchCardNumber.trim() ||
            !this.state.scratchCardPIN.trim()
          ) {
            this.setState({
              isFetchInProgress: false,
              scratchCard_error: <Gettext>All fields are required</Gettext>
            });

            return;
          }
        }

        // Когда надо было дополнить данные профиля
        if (currentBilling.needsFullName || currentBilling.needsPhoneNumber) {
          const errors = {};

          // А ты ни имени своего,
          if (
            currentBilling.needsFullName &&
            typeof this.state.fullName === "string" &&
            this.state.fullName.trim() === ""
          ) {
            errors.fullName_error = (
              <Gettext>Error! Fill in all fields!</Gettext>
            );
          }

          // Ни телефона не указал
          if (
            currentBilling.needsPhoneNumber &&
            typeof this.state.phoneNumber === "string" &&
            this.state.phoneNumber.trim() === ""
          ) {
            errors.phoneNumber_error = <Gettext>Enter your phone!</Gettext>;
          }

          // Ну что же ты за человек?
          // Вот и смотри теперь на ошибки.
          if (Object.keys(errors).length > 0) {
            this.setState({ isFetchInProgress: false, ...errors });

            if (profileFieldsContainer) {
              profileFieldsContainer.scrollIntoView({
                block: "start",
                behavior: "smooth"
              });
            }

            return;
          }

          this.props
            .fetch(
              `/api/update-profile-payment?first_name=${this.state.fullName ||
                ""}&phone=${this.state.phoneNumber || ""}`,
              { unstable_dontInferResponse: true }
            )
            .then(json => {
              if (json.status === "OK") {
                this.submit();
                return;
              }
              //TODO Костыль на скорую руку, ошибка приходит с сервера, мы ее расчленяем жутким хардкодом, так как образца для телефона нет
              if (!this.state.general_error) {
                return this.setState({
                  phoneNumber_error: json.message.split("!")[0] + "!"
                });
              }

              this.setState({ general_error: json.message });
            });

          return;
        }

        // На этом месте все валидации либо
        // прошли, либо не нужны. Отправляем.

        this.submit();
      }
    );
  };

  success_RBKpayment = async () => {
    console.log("Payment successful finished");
    const realAccount = this.props.accounts.find(acc => acc.type === "real");

    // просто редиректим на трейд если нету реального аккаунта,
    // или он уже активен
    if (!realAccount || realAccount.isActive === true) {
      this.setState({ isFetchInProgress: false });
      return this.props.history.push("/trade");
    }

    const data = await this.props.fetch(
      `/ajax/setAccount?id=${realAccount.id}`
    );
    if (data.status === "OK") {
      this.props.dispatch({ type: "SWITCH_ACCOUNTS" });
    }
    const payload = await this.props.fetch("/api/profile");
    this.props.dispatch({ type: "RECEIVE_PROFILE", payload });
    this.setState({ isFetchInProgress: false });
    this.props.history.push("/trade");
  };

  special_fetchRBKInvoice = () => {
    // Форк функции aubmit, специфичный для метода оплаты RBK
    const currentBilling = toDefault(
      this.state.billingType,
      this.props.config.defaultBilling
    );

    const currentDeposit = toDefault(
      this.state.deposit,
      parseFloat(this.props.config.defaultDeposit) ||
        this.props.config.getDefaultValue(currentBilling)
    );

    const isPredefinedValue = this.props.config.deposits.some(
      deposit => deposit.amount === currentDeposit
    );

    const bonus = this.state.isBonusApplied
      ? getPaymentBonus(this.props.config, currentDeposit) +
        getBillingBonus(this.props.config, currentBilling)
      : 0;

    let params = {
      src_layout: `fillup${this.props.config.layout}`,
      amountPercent: bonus,
      amount: currentDeposit,
      billing: this.props.config.billings[currentBilling].inputValue,
      radio: isPredefinedValue ? "on" : "off",
      bank_code:
        idx(this.props.config.banksSettings, _ => _[currentBilling].banks) &&
        this.state.bank,
      payment_point:
        idx(
          this.props.config.banksSettings,
          _ => _[currentBilling].payment_points
        ) && this.state.bank,
      no_bonus: this.state.isBonusApplied ? 0 : 1,
      ptest: this.props.config.ptest ? 1 : 0,
      coupon:
        this.state.isBonusApplied && this.state.promo != null
          ? this.state.promoCode
          : undefined
    };

    const fetchParams = {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: querystring.stringify(params)
    };

    return this.props.fetch(`/billing`, fetchParams).then(data => {
      if (data.status !== "OK") {
        this.setState({
          server_error: data.message || data.msg,
          isFetchInProgress: false
        });
        return console.error(`ServerError: ${data.message || data.msg}`);
      } else {
        return data.data.widget ? data.data.widget : data.data;
      }
    });
  };

  special_RBKonSubmit = invoice => {
    const currentBilling = toDefault(
      this.state.billingType,
      this.props.config.defaultBilling
    );

    const currentDeposit = toDefault(
      this.state.deposit,
      parseFloat(this.props.config.defaultDeposit) ||
        this.props.config.getDefaultValue(currentBilling)
    );

    const bonus = this.state.isBonusApplied
      ? getPaymentBonus(this.props.config, currentDeposit) +
        getBillingBonus(this.props.config, currentBilling)
      : 0;

    gaReducer("CLICKBTN_FILLUP", {
      paymentMethod: currentBilling,
      amount: currentDeposit,
      bonusAmount: bonus
    });

    window.RbkmoneyCheckout.configure({
      ...invoice,
      opened: () => {
        console.log("Checkout opened");
      },
      closed: () => {
        console.log("Checkout closed");
        this.setState({ isFetchInProgress: false });
      },
      finished: () => {
        this.success_RBKpayment();
      },
      popupMode: !isIronTerminal()
    }).open();
  };

  submit = () => {
    this.shouldAnimateChatButton = false;
    const billingAtNewWindow =
      this.userPlatform() === "web-mobile" ||
      this.userPlatform() === "web-desktop";

    const currentBilling = toDefault(
      this.state.billingType,
      this.props.config.defaultBilling
    );

    const currentDeposit = toDefault(
      this.state.deposit,
      parseFloat(this.props.config.defaultDeposit) ||
        this.props.config.getDefaultValue(currentBilling)
    );

    const isPredefinedValue = this.props.config.deposits.some(
      deposit => deposit.amount === currentDeposit
    );

    const bonus = this.state.isBonusApplied
      ? getPaymentBonus(this.props.config, currentDeposit) +
        getBillingBonus(this.props.config, currentBilling)
      : 0;

    if (currentBilling !== "kerry") {
      gaReducer("CLICKBTN_FILLUP", {
        paymentMethod: currentBilling,
        amount: currentDeposit,
        bonusAmount: bonus
      });
    }

    let params;

    if (currentBilling === "ipaytotal") {
      params = {
        src_layout: `fillup${this.props.config.layout}`,
        amountPercent: bonus,
        amount: currentDeposit,
        billing: this.props.config.billings[currentBilling].inputValue,
        no_bonus: this.state.isBonusApplied ? 0 : 1,
        coupon:
          this.state.isBonusApplied && this.state.promo != null
            ? this.state.promoCode
            : undefined,
        ...this.state.creditCardData
      };
    } else if (this.props.config.billings[currentBilling].is_scratch === true) {
      // Особенные параметры для типа оплаты "скретч-карта"
      const {
        scratchCardProvider,
        scratchCardNumber,
        scratchCardPIN
      } = this.state;

      const providersKeys = Object.keys(
        this.props.config.billings[currentBilling].providers
      );
      const firstScratchProvider = providersKeys[0];

      params = {
        src_layout: `fillup${this.props.config.layout}`,
        provider: scratchCardProvider || firstScratchProvider,
        serial_number: scratchCardNumber,
        pin_code: scratchCardPIN,
        billing: this.props.config.billings[currentBilling].inputValue,
        no_bonus: this.state.isBonusApplied ? 0 : 1
      };
    } else {
      params = {
        src_layout: `fillup${this.props.config.layout}`,
        amountPercent: bonus,
        amount: currentDeposit,
        billing: this.props.config.billings[currentBilling].inputValue,
        radio: isPredefinedValue ? "on" : "off",
        bank_code:
          idx(this.props.config.banksSettings, _ => _[currentBilling].banks) &&
          this.state.bank,
        payment_point:
          idx(
            this.props.config.banksSettings,
            _ => _[currentBilling].payment_points
          ) && this.state.bank,
        no_bonus: this.state.isBonusApplied ? 0 : 1,
        ptest: this.props.config.ptest ? 1 : 0,
        coupon:
          this.state.isBonusApplied && this.state.promo != null
            ? this.state.promoCode
            : undefined,
        provider:
          idx(
            this.props.config.banksSettings,
            _ => _[currentBilling].providers
          ) && this.state.bank,
        customer_pan_number:this.state.customer_pan_number,
        vodafoneCode: this.state.vodafoneCode || undefined
      };
    }

    // кастомный редирект для Kerry Express
    if (currentBilling === "kerry") {
      this.props.history.push(
        `/billing/kerry-express?${querystring.stringify(params)}`,
        {
          ...params,
          bonus
        }
      );
      return;
    }

    const fetchParams = {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: querystring.stringify(params)
    };

    this.props
      .fetch(`/billing`, fetchParams)
      .then(data => {
        if (data.status !== "OK") {
          this.setState({
            server_error: data.message || data.msg,
            isFetchInProgress: false
          });
          return console.error(`ServerError: ${data.message || data.msg}`);
        }
        //хардкод ключей из биллинг-конфига для редиректа на страницу оффлайн-платежа, если продолжаешь хардкодить сюда, то не забудь и про offline-billing.js
        if (
          [
            "offline",
            "offline_vnd",
            "offline_php",
            "atm",
            "offlineBankingVND",
            "offlineBankingPHP",
            "offlineBanking"
          ].some(type => type === currentBilling)
        ) {
          const query = querystring.stringify({
            type: currentBilling,
            payment_id: data.payment_id
          });

          this.setState({
            redirectTo: `/billing/offline?${query}&${querystring.stringify(
              params
            )}`,
            isFetchInProgress: true // Keep it true to still block buttons until redirect
          });
        } else if (currentBilling === "rbkmoney") {
          const invoice = data.data.widget ? data.data.widget : data.data;
          window.RbkmoneyCheckout.configure({
            ...invoice,
            opened: () => {
              console.log("Checkout opened");
            },
            closed: () => {
              console.log("Checkout closed");
              this.setState({ isFetchInProgress: false });
            },
            finished: () => {
              this.success_RBKpayment();
            }
          }).open();
        } else if (currentBilling === "ipaytotal") {
          // Биллинг ipaytotal требует передачи данных карты, поэтому из осто-
          // рожности мы договорились перенаправлять клиента запросом POST
          submitPost(data.redirect);
        } else if (currentBilling === "dusupay_mobile_money") {
          if (billingAtNewWindow && window.open(data.redirect)) {
            this.setState({
              isFetchInProgress: false
            });
          } else {
            window.location.href = data.redirect;
          }
        } else {
          // this.setState({
          //   redirectTo: data.redirect
          // })
          if (billingAtNewWindow && window.open(data.redirect)) {
            this.setState({
              isFetchInProgress: false
            });
          } else {
            window.location.href = data.redirect;
          }
        }
      })
      .catch(error => {
        console.log(error);
        this.setState({ isFetchInProgress: false });
      });
  };

  handleRemovePromo = () => {
    this.setState({ promoCode: "", promo: null });
    try {
      if (localStorage.getItem("promocode") != null) {
        localStorage.removeItem("promocode");
      }
    } catch (error) {
      console.error(error);
    }
  };

  handleShowPromoModal = () => {
    this.setState({ isPromoModalActive: true });
    if (document.body) document.body.style.overflow = "hidden";
  };

  toggleInfoModal = () => {
    this.setState({ isInfoModalShown: !this.state.isInfoModalShown }, () => {
      if (this.state.isInfoModalShown) {
        if (document.body) document.body.style.overflow = "hidden";
      } else {
        if (document.body) document.body.style.overflow = "";
      }
    });
  };

  handleHidePromoModal = () => {
    this.state.promo_error === null
      ? this.setState({ isPromoModalActive: false })
      : this.setState({
          isPromoModalActive: false,
          promoCode: "",
          promo_error: null
        });
    if (document.body) document.body.style.overflow = "";
  };

  handlePromoCode = value => {
    const val = value.trim();
    const matched = val.match(/[a-zA-Z0-9]+/g) || [];

    this.setState({ promoCode: matched.join("") });

    if (matched.length > 0) {
      this.setState({ promo_error: null });
    }
  };

  handleValidatePromo = () => {
    let { promoCode } = this.state;
    const locale = idx(this.props.profile, profile => profile.locale) || "en";

    this.setState({ isFetchPromoInProgress: true });

    return this.props.api
      .fetchEntity(`/api/promo-code-info?code=${promoCode}&lang=${locale}`)
      .then(entity => {
        if (entity.status !== "Rejected" && entity.value.status === "ERROR") {
          this.promoErrorCount++;

          if (this.promoErrorCount > 1 && this.shouldAnimateChatButton) {
            animateChatButton();
            this.shouldAnimateChatButton = false;
          }

          this.setState(
            {
              promo_error: entity.value.message,
              isFetchPromoInProgress: false,
              promo: null,
              promoCode: ""
            },
            () => {
              this.setState({ isPromoModalActive: true });
            }
          );

          writePromoCode("");

          return entity.value;
        } else {
          this.promoErrorCount = 0;

          this.setState({
            isFetchPromoInProgress: false,
            promo: entity.value
          });

          writePromoCode(promoCode);

          return entity.value;
        }
      });
  };

  closeInrModal = () => {
    this.setState({
      showInrInfo: false
    });
  };

  componentDidMount() {
    const layout = this.props.config && this.props.config.layout;
    if (layout) {
      this.props.fetch(`/api/track-deposit-form?layout=${layout}`);
    }

    if (this.state.promoCode !== "") {
      this.setState({ isInitialFetchPromoInProgress: true });

      let query = querystring.parse(window.location.search.slice(1));
      if (query.promocode) {
        this.handleValidatePromo().then(
          () => {
            this.setState({
              isPromoModalActive: true,
              isInitialFetchPromoInProgress: false
            });
          },
          () => {
            this.setState({
              isPromoModalActive: true,
              isInitialFetchPromoInProgress: false
            });
          }
        );
      } else {
        this.handleValidatePromo().then(
          () => {
            this.setState({
              isPromoModalActive: false, // <--- отличие!
              isInitialFetchPromoInProgress: false
            });
          },
          () => {
            this.setState({
              isPromoModalActive: true,
              isInitialFetchPromoInProgress: false
            });
          }
        );
      }
    }

    if (this.props.profile != null) {
      const { first_name, phone } = this.props.profile;
      //Если поля существуют и не пустые строки то в стейте ставим
      //их в null(!) для дальнейшего перфоманса с проверками

      this.setState({
        fullName: isValidTextValue(first_name) ? null : "",
        phoneNumber: isValidTextValue(phone) ? null : ""
      });
    }

    if (
      window.androidApp &&
      typeof window.androidApp.depositReady === "function"
    ) {
      window.androidApp.depositReady();
    }

    this.chatAnimationTimer = setTimeout(() => {
      if (this.shouldAnimateChatButton) {
        animateChatButton();
      }
    }, 90000);

    this.props
      .fetch(`/api/incomplete-offline-payments/`, {
        unstable_dontInferResponse: true
      })
      .then(data => {
        this.setState({
          offlinePaymentId: (data.status === "OK" && data.latest) || null
        });
      });

    this.props
      .fetch(`/api/offline-payment/banks`, {
        unstable_dontInferResponse: true
      })
      .then(offlineBanks => this.setState({ offlineBanks }));
  }

  componentDidUpdate(prevProps) {
    if (
      idx(prevProps.profile, profile => profile.locale) !==
        idx(this.props.profile, profile => profile.locale) &&
      this.state.promoCode !== ""
    ) {
      this.handleValidatePromo();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.chatAnimationTimer);
  }

  render() {
    if (Object.keys(this.props.config.billings).length === 0) {
      return [
        <Header />,
        <ErrorWrapper>
          <IconWrapper>
            <WarningIcon width="100%" />
          </IconWrapper>
          <ErrorMess>
            <Center>
              Sorry, but it seems like your currency is not payable.
            </Center>
          </ErrorMess>
        </ErrorWrapper>
      ];
    }

    if (this.props.profile == null) {
      return (
        <React.Fragment>
          <Header />
          Login please.
        </React.Fragment>
      );
    }

    const currentBilling = toDefault(
      this.state.billingType,
      this.props.config.defaultBilling
    );

    const currentDeposit = toDefault(
      this.state.deposit,
      parseFloat(this.props.config.defaultDeposit) ||
        this.props.config.getDefaultValue(currentBilling)
    );

    const devLayout =
      querystring.parse(window.location.search.slice(1)).layout &&
      querystring.parse(window.location.search.slice(1)).layout;

    const layoutNumber = devLayout || this.props.config.layout || "4";

    const Layout = this.state.displayCreditCardForm
      ? payTotalLayout
      : layouts[layoutNumber];

    const currency = this.props.profile.currency;
    const currencySettings = this.props.apiConfigs.currencySettings;
    const settingsForCurrency = currencySettings[currency];

    const billing = this.props.config.billings[currentBilling];

    let minimumValue = 0;

    if (billing != null && billing.min_amount != null) {
      minimumValue = billing.min_amount;
    } else if (settingsForCurrency) {
      minimumValue = settingsForCurrency.minFillup || 0;
    }

    const fillup: Fillup = {
      error: this.state.server_error,
      isSlipActive: this.state.offlineBanks && this.state.offlinePaymentId,
      setShowOfflinePopup: this.setShowOfflinePopup,
      locale: this.props.profile && this.props.profile.locale,
      deposit_count:
        this.props.profile &&
        this.props.profile.finance &&
        this.props.profile.finance.deposit_count,
      config: this.props.config,
      isIOS: this.props.serverData && this.props.serverData.isIOS,
      appVersion: this.props.serverData && this.props.serverData.appVersion,
      currencyData: {
        currency: currency,
        symbol: settingsForCurrency && settingsForCurrency.rawSymbol
      },
      currency,
      minimumValue,
      deposit:
        layoutNumber === "14" && this.state.deposit == null
          ? 0
          : currentDeposit, // депозит и способ оплаты должны
      billingType: currentBilling, // быть определены однозначно
      customer_pan_number: this.state.customer_pan_number,

      bank: this.state.bank,
      requestPhone: this.state.requestPhone,
      vodafoneCode: this.state.vodafoneCode,
      fullName: this.state.fullName,
      phoneNumber: this.state.phoneNumber,
      phoneNumberProps: this.props.profile && this.props.profile.phone,
      phoneVerified: this.state.phoneVerified,
      scratchCardProviders: (billing && billing.providers) || {},
      scratchCardProvider: this.state.scratchCardProvider,
      scratchCardNumber: this.state.scratchCardNumber,
      scratchCardPIN: this.state.scratchCardPIN,
      isBonusApplied: this.state.isBonusApplied,
      scratchCard_error: this.state.scratchCard_error,
      general_error: this.state.general_error,
      fullName_error: this.state.fullName_error,
      pan_error: this.state.pan_error,
      phoneNumber_error: this.state.phoneNumber_error,

      onNextPaymentValue: this.handleNextPaymentValue,
      onNextPanValue: this.handleNextPanValue,
      onNextBillingType: this.handleNextBillingType,
      onNextBank: this.handleNextBank,
      onRequestPhone: this.handleRequestPhone,
      onVodafoneCode: this.handleVodafoneCode,
      onFullNameChange: this.handleFullNameChange,
      onPhoneNumberChange: this.handlePhoneNumberChange,
      onScratchCardProviderChange: this.handleScratchCardProviderChange,
      onScratchCardNumberChange: this.handleScratchCardNumberChange,
      onScratchCardPINChange: this.handleScratchCardPINChange,
      onBonusSwitch: this.handleBonusSwitch,
      onSubmit: this.hanldleFormSubmission,
      onPayTotalSubmit: this.handleCreditCard,
      validatePhoneNumber: this.validatePhoneNumber,

      isFetchInProgress: this.state.isFetchInProgress,

      onRemovePromo: this.handleRemovePromo,
      onShowPromoModal: this.handleShowPromoModal,
      isPromoModalActive: this.state.isPromoModalActive,
      onDismissPromoModal: this.handleHidePromoModal,
      onValidatePromo: this.handleValidatePromo,
      isFetchPromoInProgress: this.state.isFetchPromoInProgress,
      promoCode: this.state.promoCode,
      onInputPromo: this.handlePromoCode,
      promo_error: this.state.promo_error,
      promo: this.state.promo,
      toggleInfoModal: this.toggleInfoModal,
      isInfoModalShown: this.state.isInfoModalShown,

      special_fetchRBKInvoice: this.special_fetchRBKInvoice,
      special_RBKonSubmit: this.special_RBKonSubmit
    };

    const layoutProps: LayoutProps = {
      ...fillup,
      fillup,
      history: this.props.history
    };

    if (this.state.isInitialFetchPromoInProgress) {
      return <Spinner />;
    }

    return (
      <React.Fragment>
        <span
          style={{ display: "none" }}
          qa_attr={`layout_${this.props.config.layout}`}
        />
        <Header />
        <PromoContext.Provider
          value={{
            onValidatePromo: this.handleValidatePromo,
            isPromoApplied: this.state.promo != null,
            isFetchPromoInProgress: this.state.isFetchPromoInProgress,
            promoCode: this.state.promoCode,
            onInputPromo: this.handlePromoCode,
            promo_error: this.state.promo_error,
            promo: this.state.promo
          }}
        >
          <Layout
            {...layoutProps}
            offlineBanks={this.state.offlineBanks}
            offlinePaymentId={this.state.offlinePaymentId}
          />

          {this.state.showOfflinePopup &&
            this.state.offlineBanks &&
            this.state.offlinePaymentId && (
              <OfflinePopup
                lang={fillup.locale}
                isiOSApp={fillup.isIOS}
                appVersion={fillup.appVersion}
                recipient={this.state.offlineBanks.account.full_name}
                banks={this.state.offlineBanks.banks}
                paymentId={this.state.offlinePaymentId}
                currency={fillup.currencyData && fillup.currencyData.currency}
                currencySymbol={fillup.currencyData.symbol}
                desktop={false}
                onClose={() => this.setShowOfflinePopup(false)}
              />
            )}
        </PromoContext.Provider>
        {this.state.redirectTo != null && (
          <Redirect push={true} to={this.state.redirectTo} />
        )}
        {this.state.server_error != null && (
          <ErrorPopup
            message={this.state.server_error}
            onDismiss={this.handleErrorPopupDismiss}
          />
        )}
      </React.Fragment>
    );
  }
}

function ConfiguredFillup(props) {
  return (
    <FillupConfiguration
      locale={props.profile.locale}
      currency={props.profile.currency}
      location={props.location}
    >
      {config => (
        <TournamentApi.Consumer>
          {api => (
            <Fillup
              config={config}
              key={idx(props.profile, profile => profile.id)}
              apiConfigs={props.apiConfigs}
              serverData={props.serverData}
              currencySettings={props.serverData}
              profile={props.profile}
              location={props.location}
              match={props.match}
              history={props.history}
              fetch={get}
              api={api}
              accounts={props.accounts}
              dispatch={props.dispatch}
            />
          )}
        </TournamentApi.Consumer>
      )}
    </FillupConfiguration>
  );
}

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-x: auto;
`;

const Container = styled.div`
  display: flex;
  flex-grow: 1;
  flex-shrink: 0;
  padding-top: 50px;
  text-align: center;
  background: ${({ theme }) =>
    process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
      ? "#3B9478 linear-gradient( to top, #55c784 0%, #1b546a 100% );"
      : `url(${theme.indexHeaderBackground}) no-repeat 50% 0`};
  background-size: cover;

  @media (max-width: 959px) {
    padding-top: 25px;
  }

  @media (max-width: 959px) and (min-width: 480px) {
    padding-bottom: 25px;
  }
`;

const FormContainer = styled.div`
  margin: 0 auto;
  min-width: 450px;
  width: 450px;

  @media (max-width: 480px) {
    min-width: 340px;
    width: 340px;
  }

  @media (max-width: 360px) {
    min-width: 300px;
    width: 300px;
  }
`;

export class UnauthorizedView extends React.Component<
  *,
  { activeTab: string }
> {
  state = { activeTab: this.props.defaultTab || "login" };

  static getDerivedStateFromProps(nextProps) {
    if (this == null || nextProps.defaultTab !== this.props.defaultTab) {
      return {
        activeTab: nextProps.defaultTab
      };
    } else {
      return null;
    }
  }

  dispatch = action => {
    if (action.type === "DISPLAY_AUTH_TAB") {
      this.setState({ activeTab: action.payload });
    }
  };

  componentDidMount() {
    if (
      window.androidApp &&
      typeof window.androidApp.depositReady === "function"
    ) {
      window.androidApp.depositReady();
    }
  }

  render() {
    return (
      <Wrapper>
        <Header hideTradeButton={true} />
        <Container>
          <FormContainer>
            <Server.Consumer preload={["/api/server-data", "/api/countries"]}>
              {api => (
                <RawAuth
                  geo={api.get("/api/server-data").geo}
                  apiCountries={
                    process.env.REACT_APP_DEPLOY_TARGET === "siamoption"
                      ? undefined
                      : api.get("/api/countries")
                  }
                  isVIP={false}
                  activeTab={this.state.activeTab}
                  showSocial={true}
                  ref_user={null}
                  dispatch={this.dispatch}
                  profile={this.props.profile}
                  query={this.props.query}
                  email={this.props.query.email}
                  location={this.props.location}
                  history={this.props.history}
                  type={
                    process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
                    "newIndex"
                  }
                />
              )}
            </Server.Consumer>
          </FormContainer>
        </Container>
      </Wrapper>
    );
  }
}

function _AuthorizedView(props) {
  return (
    <ConfiguredFillup
      serverData={props.serverData}
      apiConfigs={props.apiConfigs}
      profile={props.profile}
      location={props.location}
      match={props.match}
      history={props.history}
      accounts={props.accounts}
      dispatch={props.dispatch}
    />
  );
}

const AuthorizedView = Private(
  withData([
    "serverData",
    "apiConfigs",
    "profile",
    "remoteTradeSettings",
    "accounts"
  ])(connect()(_AuthorizedView))
);

export default function FillupPage(props: *) {
  const MemoFillupPage = useMemo(() => {
    return (
      <AuthorizedView
        location={props.location}
        match={props.match}
        history={props.history}
      />
    );
  }, []);

  return MemoFillupPage;
}



// WEBPACK FOOTER //
// ./src/pages/fillup.js